import { useMemo } from "react";

import type { RoomNewItem_EditPageQuery } from "@/lib/gql/__generated__/graphql";

import type { FormOptionType } from "@/components/ui/form/_types/type";

import { useInitProductRoomFormModelContext } from "../../../init";
import { useRoomCreate } from "../use-update";
import { serverModelToInputModelConverter } from "./converter/server-model-to-input-model/server-model-to-input-model";

import type { ProductNewRoomFormModelContextType } from "../../../../ui/new/context/context";

type Props = {
	onCreateRoomSuccessCallback: () => void;

	serverModel: RoomNewItem_EditPageQuery;
};

export const useInitialized = ({
	serverModel,
	onCreateRoomSuccessCallback,
}: Props): ProductNewRoomFormModelContextType => {
	const inputModel = useMemo(() => serverModelToInputModelConverter(serverModel), [serverModel]);

	const queryData = useMemo(() => {
		return {
			hotelPolicy: {
				amenities: serverModel.hotelPolicy?.amenities ?? [],
				requiredPrepMillisecond: serverModel.hotelPolicy?.requiredPrepMillisecond ?? 0,
			},
			connection: {
				planNameAndIDOptions: [
					...serverModel.planList.plans.map(
						(plan) => ({ id: plan.id, label: plan.name, value: plan.planID }) satisfies FormOptionType,
					),
					...serverModel.planList.secrets.map(
						(plan) => ({ id: plan.id, label: plan.name, value: plan.planID }) satisfies FormOptionType,
					),
				],
			},
			discountList: serverModel.discountList,
		} satisfies ProductNewRoomFormModelContextType["queryData"];
	}, [serverModel]);

	const { onSetViewModel, viewModel, onResetViewModel } = useInitProductRoomFormModelContext({
		inputModel,
		queryData,
	});

	const { onCreateRoom } = useRoomCreate({ viewModel, onResetViewModel, onCreateRoomSuccessCallback });

	return {
		queryData,
		onSetViewModel,
		viewModel,
		onUpdate: onCreateRoom,
	};
};
